import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Play,
  Check,
  Clock,
  Zap,
  GitBranch,
  MessageSquare,
  HandshakeIcon,
  Building2,
  ShieldCheck,
  Bot,
  Network,
  FileCheck,
  ChevronRight,
  ArrowRight,
  Info,
  RotateCcw,
} from 'lucide-react';
import { trackSimulatorRun, trackWorkflowInteraction } from '../utils/analytics';

// Comprehensive workflow data
const workflowsData = {
  'pr-governance': {
    name: 'PR Governance',
    icon: GitBranch,
    color: '#1A3A8F',
    description: 'Automate code review workflows with AI-powered verification, policy enforcement, and intelligent reviewer assignment.',
    steps: [
      { 
        label: 'Developer submits PR', 
        duration: 800,
        detail: 'Pull request is created in GitHub/GitLab',
        agent: null 
      },
      { 
        label: 'PR Verification Agent analyzes code', 
        duration: 1500,
        detail: 'Code quality, security vulnerabilities, and best practices checked',
        agent: 'PR Verification Agent'
      },
      { 
        label: 'Policy checks run', 
        duration: 1200,
        detail: 'Compliance with coding standards and security policies verified',
        agent: 'Compliance Policy Agent'
      },
      { 
        label: 'Reviewer assignment', 
        duration: 1000,
        detail: 'Optimal reviewers selected based on expertise and availability',
        agent: 'Reviewer Routing Agent'
      },
      { 
        label: 'Merge decision logged to UTG', 
        duration: 800,
        detail: 'Decision recorded in Unified Transaction Graph for audit',
        agent: null
      },
    ],
    agents: ['PR Verification Agent', 'Compliance Policy Agent', 'Reviewer Routing Agent'],
    metrics: {
      cycleReduction: '70%',
      automationRate: '85%',
      complianceScore: '100%',
    },
  },
  'security-compliance': {
    name: 'Security & Compliance',
    icon: ShieldCheck,
    color: '#2CB1A1',
    description: 'Continuous security monitoring with automated vulnerability detection and compliance verification.',
    steps: [
      { 
        label: 'Code change detected', 
        duration: 800,
        detail: 'New commit or configuration change triggers workflow',
        agent: null 
      },
      { 
        label: 'Security Audit Agent scans', 
        duration: 2000,
        detail: 'SAST, DAST, and dependency vulnerability scanning',
        agent: 'Security Audit Agent'
      },
      { 
        label: 'Compliance mapping', 
        duration: 1500,
        detail: 'Changes mapped to SOC 2, HIPAA, PCI-DSS requirements',
        agent: 'Compliance Policy Agent'
      },
      { 
        label: 'Risk assessment generated', 
        duration: 1200,
        detail: 'Risk score calculated with remediation priorities',
        agent: 'Security Audit Agent'
      },
      { 
        label: 'Results logged to UTG', 
        duration: 800,
        detail: 'Audit trail created for compliance reporting',
        agent: null
      },
    ],
    agents: ['Security Audit Agent', 'Compliance Policy Agent'],
    metrics: {
      cycleReduction: '90%',
      automationRate: '95%',
      complianceScore: '100%',
    },
  },
  'customer-support': {
    name: 'Customer Support',
    icon: MessageSquare,
    color: '#1A3A8F',
    description: 'Intelligent ticket routing and resolution with AI-powered classification and knowledge retrieval.',
    steps: [
      { 
        label: 'Customer submits ticket', 
        duration: 800,
        detail: 'Support request received via email, chat, or portal',
        agent: null 
      },
      { 
        label: 'Ticket Classification Agent categorizes', 
        duration: 1200,
        detail: 'Intent, urgency, and sentiment analyzed',
        agent: 'Ticket Classification Agent'
      },
      { 
        label: 'Knowledge Retrieval Agent finds solutions', 
        duration: 1500,
        detail: 'Relevant articles and past resolutions retrieved',
        agent: 'Knowledge Retrieval Agent'
      },
      { 
        label: 'Response generated and reviewed', 
        duration: 1200,
        detail: 'Draft response created for agent review',
        agent: 'Knowledge Retrieval Agent'
      },
      { 
        label: 'Resolution recorded to UTG', 
        duration: 800,
        detail: 'Outcome logged and knowledge base updated',
        agent: null
      },
    ],
    agents: ['Ticket Classification Agent', 'Knowledge Retrieval Agent'],
    metrics: {
      cycleReduction: '50%',
      automationRate: '75%',
      complianceScore: '100%',
    },
  },
  'sales-deal-desk': {
    name: 'Sales Deal Desk',
    icon: HandshakeIcon,
    color: '#2CB1A1',
    description: 'Streamlined deal approval with automated validation, pricing compliance, and intelligent routing.',
    steps: [
      { 
        label: 'Sales rep submits deal', 
        duration: 800,
        detail: 'Deal submitted for approval with terms and pricing',
        agent: null 
      },
      { 
        label: 'Deal Validation Agent reviews', 
        duration: 1500,
        detail: 'Terms validated against company policies',
        agent: 'Deal Validation Agent'
      },
      { 
        label: 'Pricing compliance verified', 
        duration: 1200,
        detail: 'Discount levels and pricing structure checked',
        agent: 'Deal Validation Agent'
      },
      { 
        label: 'Approval workflow triggered', 
        duration: 1000,
        detail: 'Routed to appropriate approvers based on deal value',
        agent: 'Deal Validation Agent'
      },
      { 
        label: 'Deal logged to UTG', 
        duration: 800,
        detail: 'Complete deal history recorded for audit',
        agent: null
      },
    ],
    agents: ['Deal Validation Agent'],
    metrics: {
      cycleReduction: '60%',
      automationRate: '80%',
      complianceScore: '100%',
    },
  },
  'vendor-onboarding': {
    name: 'Vendor Onboarding',
    icon: Building2,
    color: '#1A3A8F',
    description: 'Automated vendor risk assessment with continuous monitoring and compliance verification.',
    steps: [
      { 
        label: 'Vendor application received', 
        duration: 800,
        detail: 'New vendor submits onboarding application',
        agent: null 
      },
      { 
        label: 'Vendor Risk Agent assesses', 
        duration: 2000,
        detail: 'Financial health, operational risk, and reputation analyzed',
        agent: 'Vendor Risk Agent'
      },
      { 
        label: 'Compliance documents verified', 
        duration: 1500,
        detail: 'Certifications, insurance, and legal documents checked',
        agent: 'Compliance Policy Agent'
      },
      { 
        label: 'Contract Review Agent analyzes', 
        duration: 1500,
        detail: 'Contract terms reviewed for risks and non-standard clauses',
        agent: 'Contract Review Agent'
      },
      { 
        label: 'Vendor approved, logged to UTG', 
        duration: 800,
        detail: 'Vendor onboarded with continuous monitoring enabled',
        agent: null
      },
    ],
    agents: ['Vendor Risk Agent', 'Compliance Policy Agent', 'Contract Review Agent'],
    metrics: {
      cycleReduction: '75%',
      automationRate: '85%',
      complianceScore: '100%',
    },
  },
};

const WorkflowSimulatorPage = () => {
  const navigate = useNavigate();
  const [selectedWorkflow, setSelectedWorkflow] = useState('pr-governance');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [completed, setCompleted] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const workflow = workflowsData[selectedWorkflow];
  const WorkflowIcon = workflow.icon;

  const runSimulation = () => {
    if (isRunning) return;
    
    trackSimulatorRun(workflow.name);
    setIsRunning(true);
    setCurrentStep(-1);
    setCompleted(false);
    
    let totalDelay = 0;
    
    workflow.steps.forEach((step, index) => {
      setTimeout(() => {
        setCurrentStep(index);
      }, totalDelay);
      totalDelay += step.duration;
    });
    
    setTimeout(() => {
      setCompleted(true);
      setIsRunning(false);
    }, totalDelay + 500);
  };

  const resetSimulation = () => {
    setCurrentStep(-1);
    setCompleted(false);
    setIsRunning(false);
  };

  const handleWorkflowChange = (workflowId) => {
    setSelectedWorkflow(workflowId);
    trackWorkflowInteraction('select', workflowId, workflowsData[workflowId].name);
    resetSimulation();
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Workflow Simulator | Liquicent</title>
        <meta name="description" content="See how Liquicent orchestrates AI agents across enterprise workflows. Interactive demo of workflow automation." />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2CB1A1]/10 rounded-full mb-6">
              <Play className="w-4 h-4 text-[#2CB1A1]" />
              <span className="text-sm font-medium text-[#2CB1A1]">Interactive Demo</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              Workflow Simulator
            </h1>
            <p className="text-xl text-[#475569] leading-relaxed">
              See how Liquicent orchestrates AI agents to automate enterprise workflows. 
              Select a workflow and run the simulation to see each step in action.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Simulator Panel */}
            <div className="lg:col-span-2">
              <Card 
                className="p-8 bg-white border border-[#E5E7EB] rounded-2xl"
                style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
              >
                {/* Controls */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                  <Select value={selectedWorkflow} onValueChange={handleWorkflowChange} disabled={isRunning}>
                    <SelectTrigger className="w-64 bg-white border-[#E5E7EB]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(workflowsData).map(([key, wf]) => (
                        <SelectItem key={key} value={key}>
                          {wf.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="flex gap-2">
                    {(completed || currentStep >= 0) && !isRunning && (
                      <Button 
                        onClick={resetSimulation}
                        variant="outline"
                        className="border-[#E5E7EB] text-[#64748B]"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                      </Button>
                    )}
                    <Button 
                      onClick={runSimulation}
                      disabled={isRunning}
                      className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
                    >
                      {isRunning ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Run Simulation
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Workflow Header */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#E5E7EB]">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${workflow.color}15` }}
                  >
                    <WorkflowIcon className="w-7 h-7" style={{ color: workflow.color }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]">{workflow.name}</h2>
                    <p className="text-sm text-[#64748B]">{workflow.steps.length} steps • {workflow.agents.length} agents</p>
                  </div>
                </div>

                {/* Workflow Description */}
                <p className="text-[#475569] mb-6">{workflow.description}</p>

                {/* Steps */}
                <div className="space-y-3">
                  {workflow.steps.map((step, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-xl transition-all duration-500 border ${
                        currentStep === index 
                          ? 'bg-[#2CB1A1]/10 border-[#2CB1A1]/30' 
                          : currentStep > index
                            ? 'bg-[#1A3A8F]/5 border-[#1A3A8F]/20'
                            : 'bg-[#F6F7FB] border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                          currentStep > index
                            ? 'bg-[#1A3A8F] text-white'
                            : currentStep === index
                              ? 'bg-[#2CB1A1] text-white'
                              : 'bg-[#E5E7EB] text-[#64748B]'
                        }`}>
                          {currentStep > index ? (
                            <Check className="w-5 h-5" />
                          ) : currentStep === index ? (
                            <Zap className="w-5 h-5 animate-pulse" />
                          ) : (
                            <span className="text-sm font-semibold">{index + 1}</span>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <p className={`font-medium transition-colors duration-300 ${
                            currentStep >= index ? 'text-[#0F172A]' : 'text-[#64748B]'
                          }`}>
                            {step.label}
                          </p>
                          {showDetails && (
                            <p className="text-sm text-[#64748B] mt-1">{step.detail}</p>
                          )}
                          {step.agent && showDetails && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#1A3A8F] mt-2 px-2 py-1 bg-[#1A3A8F]/5 rounded">
                              <Bot className="w-3 h-3" />
                              {step.agent}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Completion Message */}
                {completed && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-[#1A3A8F]/5 to-[#2CB1A1]/5 rounded-xl border border-[#2CB1A1]/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#2CB1A1] flex items-center justify-center">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#0F172A] text-lg">Workflow Complete</p>
                        <p className="text-[#475569] mt-1">
                          All decisions have been recorded in the Unified Transaction Graph. 
                          Full audit trail available for compliance reporting.
                        </p>
                        <Link 
                          to={`/workflows/${selectedWorkflow}`}
                          className="inline-flex items-center text-[#1A3A8F] font-medium mt-3 hover:underline"
                        >
                          View full workflow details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Metrics Card */}
              <Card 
                className="p-6 bg-white border border-[#E5E7EB] rounded-xl"
                style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
              >
                <h3 className="font-semibold text-[#0F172A] mb-4">Expected Outcomes</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#64748B]">Cycle Time Reduction</span>
                      <span className="font-semibold text-[#2CB1A1]">{workflow.metrics.cycleReduction}</span>
                    </div>
                    <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#2CB1A1] rounded-full"
                        style={{ width: workflow.metrics.cycleReduction }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#64748B]">Automation Rate</span>
                      <span className="font-semibold text-[#1A3A8F]">{workflow.metrics.automationRate}</span>
                    </div>
                    <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#1A3A8F] rounded-full"
                        style={{ width: workflow.metrics.automationRate }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#64748B]">Compliance Score</span>
                      <span className="font-semibold text-[#2CB1A1]">{workflow.metrics.complianceScore}</span>
                    </div>
                    <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#2CB1A1] rounded-full"
                        style={{ width: workflow.metrics.complianceScore }}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Agents Used */}
              <Card 
                className="p-6 bg-white border border-[#E5E7EB] rounded-xl"
                style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
              >
                <h3 className="font-semibold text-[#0F172A] mb-4">Agents Used</h3>
                <div className="space-y-2">
                  {workflow.agents.map((agent) => (
                    <Link 
                      key={agent}
                      to={`/agents/${agent.toLowerCase().replace(/ /g, '-')}`}
                      className="flex items-center gap-3 p-3 bg-[#F6F7FB] rounded-lg hover:bg-[#1A3A8F]/5 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-[#1A3A8F]" />
                      </div>
                      <span className="text-sm text-[#475569] group-hover:text-[#1A3A8F] transition-colors flex-1">
                        {agent}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1A3A8F]" />
                    </Link>
                  ))}
                </div>
              </Card>

              {/* UTG Info */}
              <Card 
                className="p-6 bg-[#0F172A] text-white rounded-xl"
                style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.2)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Network className="w-6 h-6 text-[#2CB1A1]" />
                  <h3 className="font-semibold">Unified Transaction Graph</h3>
                </div>
                <p className="text-sm text-[#94A3B8] mb-4">
                  Every decision in this workflow is recorded in the UTG, creating a complete 
                  audit trail for compliance and governance.
                </p>
                <Link 
                  to="/research/unified-transaction-graph"
                  className="text-[#2CB1A1] text-sm font-medium hover:underline inline-flex items-center"
                >
                  Learn about UTG
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Card>

              {/* CTA */}
              <Card 
                className="p-6 bg-gradient-to-br from-[#1A3A8F] to-[#142C6D] text-white rounded-xl"
              >
                <h3 className="font-semibold mb-2">Try With Your Data</h3>
                <p className="text-sm text-white/80 mb-4">
                  See how this workflow performs with your actual processes.
                </p>
                <Button 
                  onClick={() => navigate('/request-demo')}
                  className="w-full bg-white text-[#1A3A8F] hover:bg-[#F6F7FB]"
                >
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* All Workflows */}
      <section className="py-12 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8">All Workflows</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(workflowsData).map(([key, wf]) => {
              const WfIcon = wf.icon;
              return (
                <Card 
                  key={key}
                  className={`p-6 border rounded-xl transition-all cursor-pointer ${
                    selectedWorkflow === key 
                      ? 'border-[#1A3A8F] bg-[#1A3A8F]/5' 
                      : 'border-[#E5E7EB] hover:border-[#1A3A8F]/30 hover:shadow-md'
                  }`}
                  onClick={() => { setSelectedWorkflow(key); resetSimulation(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${wf.color}15` }}
                    >
                      <WfIcon className="w-6 h-6" style={{ color: wf.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#0F172A] mb-1">{wf.name}</h3>
                      <p className="text-sm text-[#64748B]">{wf.steps.length} steps • {wf.agents.length} agents</p>
                    </div>
                    {selectedWorkflow === key && (
                      <div className="w-6 h-6 rounded-full bg-[#1A3A8F] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkflowSimulatorPage;
